import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import MachineTranslationsDialog from '@amberscript/amberscript-fe-components/dist/components/MachineMadeTranslations/machineTranslationsDialog';
import { useRecoilState } from 'recoil';
import { trackSentryErrorByTag } from '@amberscript/amberscript-fe-components/dist/sentry';

import { machineTranslationsAtom } from './store/machineTranslations';
import {
  createMachineTranslationsJob,
  getMachineTranslationLanguages,
  moveTranslationJobsToQueue,
} from './api/machineTranslations';
import { DASHBOARD_URL } from '../../settings';

export default function MachineTranslations(props) {
  const { isTranslateModalOpen, setTranslateModalOpen, currentJobId, originalJobId, translate, jobLanguage } = props;
  // Use original job ID for translation API so we can request translation when viewing a translation (e.g. DE→EN then EN→FR)
  const sourceJobIdForTranslation = originalJobId || currentJobId;
  const [machineTranslation, setMachineTranslation] = useRecoilState(
    machineTranslationsAtom,
  );

  const closeMachineMadeDialog = () => {
    setTranslateModalOpen(false)
  };

 
  // Get the supported and target languages for machine translations
  async function getAvailableLanguages() {
    try {
      const result = await getMachineTranslationLanguages();
      if (result && result.data) {
        const { sourceLanguages, targetLanguages } = result.data;
        setMachineTranslation({
          ...machineTranslation,
          jobId: sourceJobIdForTranslation,
          jobLanguage: jobLanguage || '',
          open: true,
          sourceLanguages,
          targetLanguages,
        });
      }
    } catch (error) {
      trackSentryErrorByTag(error, 'auto-translation-languages');
      // Don't set open to true if there's an error to prevent white screen
      setMachineTranslation({
        ...machineTranslation,
        open: false,
      });
    }
  }

  useEffect(() => {
    if (isTranslateModalOpen && sourceJobIdForTranslation) {
      getAvailableLanguages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTranslateModalOpen, sourceJobIdForTranslation, jobLanguage]);

  // Orders machine translations (always use original job ID so translation works from any language variant)
  const handleOrder = async (languages) => {
    const costCenter = '';
    const jobId = machineTranslation.jobId || sourceJobIdForTranslation;
    try {
      const result = await createMachineTranslationsJob(
        jobId,
        languages,
        costCenter,
      );
      if (result && result.data) {
        const { jobIds } = result.data;
        await moveTranslationJobsToQueue(jobIds);
        window.location.href = `${DASHBOARD_URL}/?machineTranslationsOrdered`;
      }
    } catch (error) {
      trackSentryErrorByTag(error, 'auto-translation-job');
    } finally {
      closeMachineMadeDialog();
    }
  };

  if (!machineTranslation.open || !isTranslateModalOpen) {
    return null;
  }

  // Prevent rendering if required data is missing
  if (!machineTranslation.targetLanguages || !machineTranslation.sourceLanguages) {
    return null;
  }

  return (
    <MachineTranslationsDialog
      open={isTranslateModalOpen}
      handleClose={closeMachineMadeDialog}
      jobId={sourceJobIdForTranslation}
      targetLanguages={machineTranslation.targetLanguages || []}
      sourceLanguage={machineTranslation.jobLanguage || jobLanguage || ''}
      handleOrder={handleOrder}
      translate={translate}
    />
  );
}

MachineTranslations.propTypes = {
  isTranslateModalOpen: PropTypes.bool,
  setTranslateModalOpen: PropTypes.func,
  currentJobId: PropTypes.string,
  originalJobId: PropTypes.string,
  translate: PropTypes.func,
  jobLanguage: PropTypes.string,
};
